import type { CartLineItem, Product, ProductVariant } from '~/types'
import {
  cartLineToGtmItem,
  productToGtmItem,
  type GtmPurchasePayload,
} from '~/utils/gtmEcommerce'
import {
  buildMetaAddToCartParams,
  buildMetaInitiateCheckoutParams,
  buildMetaPurchaseParams,
  buildMetaViewContentParams,
  metaPurchaseEventId,
} from '~/utils/metaEcommerce'
import { isMetaPixelInitialized, trackMetaEvent } from '~/utils/metaPixel'

export function useMetaEcommerce() {
  const { metaPixel } = useSiteSettings()
  const { marketing } = useCookieConsent()

  function canTrack(): boolean {
    return Boolean(
      import.meta.client
      && metaPixel.value.enabled
      && metaPixel.value.pixelIds.length > 0
      && marketing.value
      && isMetaPixelInitialized(),
    )
  }

  function trackViewItem(product: Product, variant?: ProductVariant) {
    if (!canTrack()) return
    const item = productToGtmItem(product, 1, variant)
    trackMetaEvent('ViewContent', buildMetaViewContentParams(item))
  }

  function trackAddToCart(product: Product, quantity = 1, variant?: ProductVariant) {
    if (!canTrack()) return
    const item = productToGtmItem(product, quantity, variant)
    trackMetaEvent('AddToCart', buildMetaAddToCartParams(item))
  }

  function trackBeginCheckout(items: CartLineItem[], value: number) {
    if (!canTrack() || !items.length) return
    trackMetaEvent(
      'InitiateCheckout',
      buildMetaInitiateCheckoutParams(items.map(cartLineToGtmItem), value),
    )
  }

  function trackPurchase(payload: GtmPurchasePayload): boolean {
    if (!canTrack()) return false

    trackMetaEvent(
      'Purchase',
      buildMetaPurchaseParams(payload),
      { eventID: metaPurchaseEventId(payload.transaction_id) },
    )
    return true
  }

  return {
    canTrack,
    trackViewItem,
    trackAddToCart,
    trackBeginCheckout,
    trackPurchase,
  }
}
